import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavArrowLeft, Search, Plus } from 'iconoir-react';
import { CreateGroupModal, GroupCard } from './components';
import { AuthenticatedLayout } from "@/components/layout/AuthenticatedLayout";

interface GroupData {
  id: string;
  name: string;
  description: string;
  rules: string;
  location: string;
  avatar?: string;
  coverImage?: string;
  memberCount: number;
}

const MyGroupPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'joined' | 'yours'>('joined');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [userGroups, setUserGroups] = useState<GroupData[]>([]);

  const handleCreateGroup = () => {
    setShowCreateModal(true);
  };

  const handleJoinGroup = () => {
    navigate('/groups');
  };

  const handleCreateGroupSubmit = (groupData: GroupData) => {
    setUserGroups(prev => [...prev, groupData]);
    setShowCreateModal(false);
  };

  const handleGroupClick = (group: GroupData) => {
    navigate(`/my_group/${group.id}`, { state: { group } });
  };

  return (
    <AuthenticatedLayout showBottomNav={true} showNavbar={false} backgroundColor="#FBF6F1">
      <div className="w-full min-h-screen bg-[#FBF6F1] flex flex-col">
        {/* Header */}
        <div className="w-full h-14 p-4 inline-flex justify-between items-start">
          <div
            className="size-7 relative overflow-hidden cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <NavArrowLeft className="h-6 w-6 text-[#1E3A3A]" />
          </div>
          <div className="justify-start text-[#1E3A3A] text-2xl font-medium font-manrope leading-7">
            Hội nhóm
          </div>
          <div className="size-7 relative opacity-0 overflow-hidden">
            <NavArrowLeft className="h-6 w-6 text-[#1E3A3A]" />
          </div>
        </div>

        {/* Search Bar */}
        <div className="w-full p-4 rounded-md inline-flex justify-between items-center">
          <div className="w-60 flex justify-start items-center gap-2">
            <div className="size-6 relative overflow-hidden">
              <Search className="w-5 h-5 text-[#1E3A3A]" />
            </div>
            <div className="justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-tight">
              Tìm kiếm hội nhóm...
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="w-full inline-flex justify-start items-center">
          <div
            className={`flex-1 p-4 flex justify-center items-center gap-2.5 cursor-pointer ${
              activeTab === 'joined' ? 'border-b border-[#1E3A3A]' : ''
            }`}
            onClick={() => setActiveTab('joined')}
          >
            <div className={`text-center justify-start text-sm font-sora leading-none ${
              activeTab === 'joined'
                ? 'text-[#1E3A3A] font-medium'
                : 'text-[#6F6F6B] font-normal'
            }`}>
              Nhóm đã tham gia
            </div>
          </div>
          <div
            className={`flex-1 p-4 flex justify-center items-center gap-2.5 cursor-pointer ${
              activeTab === 'yours' ? 'border-b border-[#1E3A3A]' : ''
            }`}
            onClick={() => setActiveTab('yours')}
          >
            <div className={`text-center justify-start text-sm font-sora leading-tight ${
              activeTab === 'yours'
                ? 'text-[#1E3A3A] font-medium'
                : 'text-[#6F6F6B] font-normal'
            }`}>
              Nhóm của bạn
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {activeTab === 'joined' ? (
            // Joined groups content
            <div className="w-full py-6 flex flex-col justify-start items-center gap-6">
              <div>
                <img src="/icons/no_groups.svg" alt="No groups" className="w-32 h-32" />
              </div>
              <div className="self-stretch text-center justify-start text-[#6F6F6B] text-sm font-normal font-sora leading-tight">
                Hiện chưa có nhóm nào
              </div>
              <div
                className="px-4 py-3.5 bg-[#E06448] rounded-full inline-flex justify-center items-center gap-2.5 cursor-pointer"
                onClick={handleJoinGroup}
              >
                <div className="size-5 relative overflow-hidden">
                  <Plus className="h-5 w-5 text-[#F4E7DA]" />
                </div>
                <div className="text-center justify-start text-[#F4E7DA] text-sm font-medium font-sora leading-none">
                  Tham gia nhóm
                </div>
              </div>
            </div>
          ) : (
            // Your groups content
            <div className="w-full py-6 flex flex-col justify-start items-center gap-6">
              {userGroups.length === 0 ? (
                <>
                  <div>
                    <img src="/icons/no_groups.svg" alt="No groups" className="w-32 h-32" />
                  </div>
                  <div className="self-stretch text-center justify-start text-[#6F6F6B] text-sm font-normal font-sora leading-tight">
                    Hiện chưa có nhóm nào
                  </div>
                  <div
                    className="px-4 py-3.5 bg-[#E06448] rounded-full inline-flex justify-center items-center gap-2.5 cursor-pointer"
                    onClick={handleCreateGroup}
                  >
                    <div className="size-5 relative overflow-hidden">
                      <Plus className="h-5 w-5 text-[#F4E7DA]" />
                    </div>
                    <div className="text-center justify-start text-[#F4E7DA] text-sm font-medium font-sora leading-none">
                      Tạo nhóm
                    </div>
                  </div>
                </>
              ) : (
                <div className="w-full px-4 flex flex-col gap-4">
                  {userGroups.map((group) => (
                    <GroupCard
                      key={group.id}
                      group={group}
                      onClick={() => handleGroupClick(group)}
                    />
                  ))}
                  <div
                    className="px-4 py-3.5 bg-[#E06448] rounded-full inline-flex justify-center items-center gap-2.5 cursor-pointer self-center"
                    onClick={handleCreateGroup}
                  >
                    <div className="size-5 relative overflow-hidden">
                      <Plus className="h-5 w-5 text-[#F4E7DA]" />
                    </div>
                    <div className="text-center justify-start text-[#F4E7DA] text-sm font-medium font-sora leading-none">
                      Tạo nhóm
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Create Group Modal */}
        <CreateGroupModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreateGroup={handleCreateGroupSubmit}
        />
      </div>
    </AuthenticatedLayout>
  );
};

export default MyGroupPage;
